// src/components/CustomerNavigation.jsx — Revised UI_v3: Admin-style header + sub-header tab bar
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { signOutCustomer } from '../api/customerAuthService';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useModules } from '../contexts/ModuleContext';
import CustomerAIHub from './customer/CustomerAIHub';
import '../styles/navigation.css';

// ─── Customer navigation group definitions ───────────────────────────────────
// Row 1 = category tabs (with monochromatic SVG icons)
// Row 2 = sub-tool pills that expand when a group tab is selected
const CUSTOMER_NAV_GROUPS = [
  {
    id: 'home',
    label: 'Home',
    mainPath: 'home',         // clicking the tab icon navigates here
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    items: [
      { label: 'Services',       path: 'home', section: 'services'       },
      { label: 'Business Hours', path: 'home', section: 'business-hours' },
    ],
  },
  {
    id: 'profile',
    label: 'My Account Profile',
    mainPath: 'profile',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
    items: [
      { label: 'Personal Information',      path: 'profile', section: 'personal-info'  },
      { label: 'Service Preferences',       path: 'profile', section: 'service-prefs'  },
      { label: 'Communication Preferences', path: 'profile', section: 'comm-prefs'     },
    ],
  },
  {
    id: 'book',
    label: 'Book Services',
    mainPath: 'book',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8"  y1="2" x2="8"  y2="6" />
        <line x1="3"  y1="10" x2="21" y2="10" />
      </svg>
    ),
    items: [
      { label: 'Book Now', path: 'book', section: null },
    ],
  },
  {
    id: 'loyalty',
    label: 'Loyalty Program',
    mainPath: 'loyalty',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    items: [
      { label: 'Points Balance',       path: 'loyalty', section: 'points-balance'       },
      { label: 'Available Vouchers',   path: 'loyalty', section: 'available-promotions' },
      { label: 'Redemption History',   path: 'loyalty', section: 'redemption-history'   },
    ],
  },
  {
    id: 'wallet',
    label: 'Wallet',
    mainPath: 'wallet',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 7H5a2 2 0 0 1 0-4h13a1 1 0 0 1 1 1v3z" />
        <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-3" />
        <path d="M21 12h-4a1.5 1.5 0 0 0 0 4h4v-4z" />
      </svg>
    ),
    items: [
      { label: 'Wallet Balance', path: 'wallet', section: 'balance' },
      { label: 'Cash Top-Up',    path: 'wallet', section: 'topup'   },
    ],
  },
  {
    id: 'packages',
    label: 'Packages',
    mainPath: 'packages',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
        <line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
    items: [
      { label: 'My Packages', path: 'packages', section: null },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────

function CustomerNavigation() {
  const { salonId }     = useParams();
  const navigate        = useNavigate();
  const { currentUser, currentCustomer } = useCustomerAuth();

  // Fixed wrapper ref — measured height drives the spacer so page content
  // starts exactly where the nav ends, on every page and every breakpoint.
  const navWrapperRef = useRef(null);
  const [navHeight, setNavHeight] = useState(120);
  useEffect(() => {
    const el = navWrapperRef.current;
    if (!el) return;
    setNavHeight(el.offsetHeight);
    const ro = new ResizeObserver(() => setNavHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Salon branding
  const [salonInfo, setSalonInfo] = useState(null);

  // Sign-out state
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Sub-header: which group tab is currently open (null = all collapsed)
  const [activeGroupId, setActiveGroupId] = useState(null);

  // Notifications
  const [notifCount,        setNotifCount]        = useState(0);
  const [notifItems,        setNotifItems]         = useState([]);
  const [showNotifDropdown, setShowNotifDropdown]  = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState(() => {
    const stored = localStorage.getItem(`notifLastSeen_${salonId}`);
    return stored ? parseInt(stored, 10) : 0;
  });
  // Ref so the listener closure always reads the current lastSeenAt without
  // needing to be in the dependency array (prevents re-subscribe on bell click).
  const lastSeenAtRef = useRef(lastSeenAt);
  useEffect(() => { lastSeenAtRef.current = lastSeenAt; }, [lastSeenAt]);

  // When the notification dropdown transitions open → closed, mark all shown
  // notifications as seen (covers bell toggle, click-outside, and item clicks).
  const wasShowingNotifRef = useRef(false);
  useEffect(() => {
    if (wasShowingNotifRef.current && !showNotifDropdown) {
      const now = Date.now();
      localStorage.setItem(`notifLastSeen_${salonId}`, String(now));
      setLastSeenAt(now);
    }
    wasShowingNotifRef.current = showNotifDropdown;
  }, [showNotifDropdown, salonId]);

  // Module availability
  const { isModuleEnabled, initializeForSalon } = useModules();
  useEffect(() => {
    if (salonId) initializeForSalon(salonId);
  }, [salonId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookingsEnabled = isModuleEnabled('bookings');
  const loyaltyEnabled  = isModuleEnabled('promotions');
  const aiEnabled       = isModuleEnabled('bespokeai');

  // Dropdown refs (click-outside)
  const profileMenuRef  = useRef(null);
  const notifDropRef    = useRef(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // ── Load salon branding ──────────────────────────────────────────────────
  useEffect(() => {
    if (!salonId) return;
    getDoc(doc(db, 'salons', salonId))
      .then(snap => { if (snap.exists()) setSalonInfo(snap.data()); })
      .catch(err => console.error('CustomerNav: salon fetch error', err));
  }, [salonId]);

  // ── Notification listeners (appointments + vouchers) ────────────────────
  useEffect(() => {
    if (!currentUser?.email || !salonId) return;

    let apptItems   = [];
    let voucherItems = [];
    let walletItems  = [];

    const merge = () => {
      const all = [...apptItems, ...voucherItems, ...walletItems].sort((a, b) => b.ts - a.ts);
      setNotifItems(all);
      setNotifCount(all.length);
    };

    // Appointments: flag status-changed records and stylist reassignments newer than lastSeenAt
    const apptUnsub = onSnapshot(
      query(
        collection(db, 'appointments'),
        where('customerEmail', '==', currentUser.email),
        where('salonId',       '==', salonId)
      ),
      snap => {
        apptItems = [];
        snap.forEach(d => {
          const a = d.data();
          const updatedMs =
            a.updatedAt?.seconds   ? a.updatedAt.seconds * 1000
            : a.updatedAt          ? new Date(a.updatedAt).getTime()
            : 0;
          if (updatedMs <= lastSeenAtRef.current) return;

          const dateLabel = a.dateTimeRequested?.toDate
            ? a.dateTimeRequested.toDate().toLocaleDateString()
            : a.dateTimeRequested
            ? new Date(a.dateTimeRequested).toLocaleDateString()
            : '';
          const serviceName = a.serviceRequested?.name || 'Service';

          if (a.staffReassigned && a.previousStaffName && a.staffName) {
            // Stylist was changed — show reassignment notification with reason
            const reason = a.staffReassignmentReason ? ` Reason: ${a.staffReassignmentReason}.` : '';
            apptItems.push({
              id:      `${d.id}_reassigned`,
              type:    'appointment_reassigned',
              text:    `Your stylist for ${serviceName}${dateLabel ? ` on ${dateLabel}` : ''} has been changed from ${a.previousStaffName} to ${a.staffName}.${reason} Please check your appointments to keep or cancel.`,
              ts:      updatedMs,
              apptId:  d.id,
            });
          } else if (['approved', 'rejected', 'cancelled'].includes(a.status)) {
            let text;
            if (a.status === 'cancelled' && a.cancellationReason) {
              text = `Appointment cancelled: ${serviceName}${dateLabel ? ` on ${dateLabel}` : ''}. Reason: ${a.cancellationReason}`;
            } else {
              text = `Appointment ${a.status}: ${serviceName}${dateLabel ? ` on ${dateLabel}` : ''}`;
            }
            apptItems.push({
              id:   d.id,
              type: 'appointment',
              text,
              ts:   updatedMs,
            });
          }
        });
        merge();
      },
      err => console.error('CustomerNav: appt listener error', err)
    );

    // Vouchers: flag records created after lastSeenAt
    const vouchUnsub = onSnapshot(
      query(
        collection(db, 'vouchers'),
        where('customerEmail', '==', currentUser.email),
        where('salonId',       '==', salonId)
      ),
      snap => {
        voucherItems = [];
        snap.forEach(d => {
          const v = d.data();
          const createdMs =
            v.createdAt?.seconds ? v.createdAt.seconds * 1000
            : v.createdAt        ? new Date(v.createdAt).getTime()
            : 0;
          if (createdMs > lastSeenAtRef.current) {
            voucherItems.push({
              id:   d.id,
              type: 'voucher',
              text: `New voucher: ${v.promotionName || 'Promotion'}${v.voucherCode ? ` — Code: ${v.voucherCode}` : ''}`,
              ts:   createdMs,
            });
          }
        });
        merge();
      },
      err => console.error('CustomerNav: voucher listener error', err)
    );

    // Wallet: flag top-up verify/reject outcomes and Wallet-paid debit transactions
    // newer than lastSeenAt (covers both the admin in-person spend path and the
    // customer's own online product purchases).
    const walletUnsub = onSnapshot(
      query(
        collection(db, 'transactions'),
        where('customerEmail', '==', currentUser.email),
        where('salonId',       '==', salonId)
      ),
      snap => {
        walletItems = [];
        snap.forEach(d => {
          const t = d.data();
          const updatedMs =
            t.updatedAt?.seconds ? t.updatedAt.seconds * 1000
            : t.updatedAt        ? new Date(t.updatedAt).getTime()
            : 0;
          if (updatedMs <= lastSeenAtRef.current) return;

          if (t.type === 'wallet_topup' && ['verified', 'rejected'].includes(t.walletTopupStatus)) {
            const amountLabel = `$${parseFloat(t.amount || 0).toFixed(2)}`;
            walletItems.push({
              id:   `${d.id}_${t.walletTopupStatus}`,
              type: 'wallet',
              text: t.walletTopupStatus === 'verified'
                ? `Your ${amountLabel} wallet top-up has been verified.`
                : `Your ${amountLabel} wallet top-up request could not be verified.`,
              ts:   updatedMs,
            });
          } else if (t.paymentMethod === 'Wallet' && t.status === 'completed') {
            const amountLabel = `$${parseFloat(t.amount || 0).toFixed(2)}`;
            walletItems.push({
              id:   `${d.id}_debit`,
              type: 'wallet',
              text: `Your wallet was debited ${amountLabel}.`,
              ts:   updatedMs,
            });
          }
        });
        merge();
      },
      err => console.error('CustomerNav: wallet listener error', err)
    );

    return () => { apptUnsub(); vouchUnsub(); walletUnsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.email, salonId]);

  // ── Click-outside handler for dropdowns ─────────────────────────────────
  useEffect(() => {
    const handler = e => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (notifDropRef.current && !notifDropRef.current.contains(e.target)) {
        setShowNotifDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Bell click: toggle dropdown ──────────────────────────────────────────
  // lastSeenAt is updated when the dropdown CLOSES (see wasShowingNotifRef
  // effect above), so opening the bell never triggers a listener re-subscribe
  // and never wipes the notifications the user is about to read.
  const handleBellClick = () => {
    setShowNotifDropdown(prev => !prev);
  };

  // ── Sign out ─────────────────────────────────────────────────────────────
  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await signOutCustomer();
      navigate(`/BespokeBeauty/${salonId}/customer/signout`);
    } catch (e) {
      console.error('CustomerNav: logout error', e);
      navigate(`/BespokeBeauty/${salonId}/customer/signout`);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // ── Sub-tab item click ───────────────────────────────────────────────────
  const handleItemClick = item => {
    navigate(
      `/BespokeBeauty/${salonId}/customer/${item.path}`,
      item.section ? { state: { section: item.section } } : undefined
    );
    // On mobile phones (≤450px: iPhone 17 family + Samsung S23 Ultra) keep the
    // sub-header row open so buttons remain visible after scrolling back up.
    // On tablets and laptops collapse as before.
    if (window.innerWidth > 450) {
      setActiveGroupId(null);
    }
  };

  // ── Derived values ───────────────────────────────────────────────────────
  const primaryColor = salonInfo?.branding?.colors?.primary || '#b6a593';
  const displayName  = currentUser?.displayName || currentUser?.email || 'C';
  const initials     = displayName.charAt(0).toUpperCase();
  const photoURL     = currentUser?.photoURL || currentCustomer?.photoURL || null;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* ═══════════ FIXED WRAPPER — both bars pinned to viewport top ═══════ */}
      <div ref={navWrapperRef} className="customer-nav-fixed-wrapper">

      {/* ═══════════════ MAIN HEADER BAR ═══════════════════════════════════ */}
      <div className="customer-header-bar" style={{ backgroundColor: primaryColor }}>
        <div className="customer-header-content">

          {/* Left: brand + portal title */}
          <div className="customer-header-brand">
            <h2 className="customer-header-brand-name">BespokeBeauty</h2>
            <h1 className="customer-header-portal-title">Customer Portal</h1>
          </div>

          {/* Right zone: quick-action buttons, bell, avatar */}
          <div className="customer-header-right">

            {/* Quick-action: View Appointments */}
            <button
              className="customer-header-quick-btn"
              disabled={!bookingsEnabled}
              style={!bookingsEnabled ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
              onClick={() => navigate(`/BespokeBeauty/${salonId}/customer/appointments`)}
            >
              View Appointments
            </button>

            {/* Quick-action: View Vouchers */}
            <button
              className="customer-header-quick-btn"
              disabled={!loyaltyEnabled}
              style={!loyaltyEnabled ? { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
              onClick={() => navigate(
                `/BespokeBeauty/${salonId}/customer/loyalty`,
                { state: { section: 'available-promotions' } }
              )}
            >
              View Vouchers
            </button>

            {/* Notification bell */}
            <div className="customer-notif-wrapper" ref={notifDropRef}>
              <button
                className="customer-notif-btn"
                onClick={handleBellClick}
                title={
                  notifCount > 0
                    ? `${notifCount} new notification${notifCount > 1 ? 's' : ''}`
                    : 'No new notifications'
                }
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {notifCount > 0 && (
                  <span className="customer-notif-badge">
                    {notifCount > 99 ? '99+' : notifCount}
                  </span>
                )}
              </button>

              {/* Notification dropdown */}
              {showNotifDropdown && (
                <div className="customer-notif-dropdown">
                  <div className="customer-notif-dropdown-header">Notifications</div>
                  {notifItems.length === 0 ? (
                    <p className="customer-notif-empty">No new notifications</p>
                  ) : (
                    <ul className="customer-notif-list">
                      {notifItems.slice(0, 10).map(n => (
                        <li
                          key={n.id}
                          className={`customer-notif-item notif-type-${n.type}`}
                          onClick={() => {
                            setShowNotifDropdown(false);
                            navigate(
                              n.type === 'voucher'
                                ? `/BespokeBeauty/${salonId}/customer/loyalty`
                                : n.type === 'wallet'
                                ? `/BespokeBeauty/${salonId}/customer/wallet`
                                : `/BespokeBeauty/${salonId}/customer/appointments`
                            );
                          }}
                        >
                          {n.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Profile avatar + dropdown */}
            <div className="customer-profile-wrapper" ref={profileMenuRef}>
              <button
                className="customer-profile-btn"
                onClick={() => setShowProfileMenu(prev => !prev)}
                title="My account"
              >
                {photoURL ? (
                  <img
                    src={photoURL}
                    alt={displayName}
                    referrerPolicy="no-referrer"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                    onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = ''; }}
                  />
                ) : null}
                <span style={photoURL ? { display: 'none' } : undefined}>{initials}</span>
              </button>

              {showProfileMenu && (
                <div className="customer-profile-menu">
                  <div className="customer-profile-menu-header">
                    <p className="customer-profile-menu-name">
                      {currentUser?.displayName || 'Customer'}
                    </p>
                    <p className="customer-profile-menu-email">
                      {currentUser?.email || ''}
                    </p>
                  </div>
                  <div className="customer-profile-menu-divider" />
                  <button
                    className="customer-profile-menu-item"
                    onClick={() => {
                      navigate(`/BespokeBeauty/${salonId}/customer/profile`);
                      setShowProfileMenu(false);
                    }}
                  >
                    My Profile
                  </button>
                  <div className="customer-profile-menu-divider" />
                  <button
                    className="customer-profile-menu-item danger"
                    onClick={() => { setShowProfileMenu(false); handleLogout(); }}
                    disabled={isLoggingOut}
                  >
                    {isLoggingOut ? 'Signing out...' : '↪ Sign Out'}
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ═══════════════ SUB-HEADER TAB BAR ════════════════════════════════ */}
      <div className="customer-top-tab-bar">

        {/* Row 1: module group tabs */}
        <div className="customer-tab-bar-groups">
          {CUSTOMER_NAV_GROUPS.map(group => {
            const isSelected = activeGroupId === group.id;
            const isDisabled =
              (group.id === 'book'    && !bookingsEnabled) ||
              ((group.id === 'loyalty' || group.id === 'wallet') && !loyaltyEnabled);
            return (
              <button
                key={group.id}
                className={`customer-tab-group-btn${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                style={isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                onClick={() => {
                  if (isDisabled) {
                    setActiveGroupId(isSelected ? null : group.id);
                    return;
                  }
                  navigate(`/BespokeBeauty/${salonId}/customer/${group.mainPath}`);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setActiveGroupId(isSelected ? null : group.id);
                }}
              >
                <span className="customer-tab-group-icon">{group.icon}</span>
                <span className="customer-tab-group-label">{group.label}</span>
              </button>
            );
          })}
        </div>

        {/* Row 2: sub-tool pills for selected group, or disabled notice */}
        {activeGroupId && (() => {
          const group = CUSTOMER_NAV_GROUPS.find(g => g.id === activeGroupId);
          if (!group) return null;

          const isDisabled =
            (group.id === 'book'    && !bookingsEnabled) ||
            ((group.id === 'loyalty' || group.id === 'wallet') && !loyaltyEnabled);

          if (isDisabled) {
            const moduleName = group.id === 'book'
              ? 'Booking Management'
              : group.id === 'wallet'
              ? 'Wallet'
              : 'Loyalty Programme';
            return (
              <div className="customer-tab-bar-items">
                <span className="customer-tab-disabled-notice">
                  {moduleName} is not enabled for this organisation.
                </span>
              </div>
            );
          }

          return (
            <div className="customer-tab-bar-items">
              {group.items.map(item => (
                <button
                  key={item.label}
                  className="customer-tab-item-btn"
                  onClick={() => handleItemClick(item)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          );
        })()}

      </div>
      </div>{/* end customer-nav-fixed-wrapper */}

      {/* Spacer pushes page content below the fixed nav on every page */}
      <div style={{ height: navHeight }} aria-hidden="true" />

      {/* Floating AI icon — bottom-right, same position/approach as Admin BespokeAI Hub */}
      <CustomerAIHub aiEnabled={aiEnabled} />
    </>
  );
}

export default CustomerNavigation;
